import { Button } from "@/components/ui/button"
import { Loader2, Sparkles, Wand2 } from "lucide-react"
import { TransactionJson } from "@/api"
import { use_coding_wiz_preferences } from "@/api/coding_wiz_preferences"
import { coding_pill_notice } from "@/utils/transaction_amount"
import { start_bulk_coding_wiz_for_rows, type BulkCodingRow } from "./bulk_coding_actions"
import { BulkCodingWizState, bulk_coding_wiz_counts, bulk_coding_wiz_is_finished } from "./bulk_coding_wiz_state"
import { capture_event } from "@/utils/analytics"

/** The rows on a check that still need a GL code: deductions, and invoices paid short by a discount. */
export function uncoded_rows(transactions: TransactionJson[]): BulkCodingRow[] {
  return transactions
    .filter((t: TransactionJson) => t.accounting_status !== "coded" && coding_pill_notice(t) === null)
    .map((t: TransactionJson) => ({
      id: t.id,
      source: t.source,
      kind: t.kind,
      invoice_amount: t.invoice_amount,
      discount_amount: t.discount_amount,
      invoice_number: t.invoice_number,
    }))
}

/** Which of the three steps a person should take next. */
export function coding_strip_stage(total: number, uncoded: number, auto_code_tried: boolean): 1 | 2 | 3 {
  if (total === 0 || uncoded === 0) return 3
  return auto_code_tried ? 2 : 1
}

/**
 * What step 3 says. With no accounting system connected there is nothing to apply to, so the step
 * points at Export CSV and the manual applied mark instead of Preview cash application.
 */
export function review_step_copy(uncoded: number, has_accounting_system: boolean): { title: string; detail: string } {
  if (!has_accounting_system) {
    return {
      title: "Review and export",
      detail:
        uncoded === 0
          ? "Every line is coded. Export CSV below, then Mark as applied."
          : "Fix any line by hand, then Export CSV and Mark as applied.",
    }
  }
  return {
    title: "Review and apply",
    detail:
      uncoded === 0
        ? "Every line is coded. Press Preview cash application above."
        : "Fix any line by hand, then Preview cash application.",
  }
}

interface CodingStripProps {
  transactions: TransactionJson[]
  /** Whether the org has QuickBooks, Xero, NetSuite or Sage Intacct connected; without one, step 3 is an export. */
  has_accounting_system: boolean
  /** Whether Preview auto code has been opened on this visit; after that, CodingWiz is the next step. */
  auto_code_tried: boolean
  on_auto_code: () => void
  className?: string
}

/**
 * The order to code a check in, as three steps above the table: rules first, CodingWiz on what is
 * left, then review and apply. The per-row controls still work; this is the obvious path.
 */
export function CodingStrip({ transactions, has_accounting_system, auto_code_tried, on_auto_code, className }: CodingStripProps) {
  const { prefs } = use_coding_wiz_preferences()
  const run = BulkCodingWizState.use()
  const rows = uncoded_rows(transactions)
  const codeable = transactions.filter((t: TransactionJson) => coding_pill_notice(t) === null).length
  const stage = coding_strip_stage(codeable, rows.length, auto_code_tried)
  const review_step = review_step_copy(rows.length, has_accounting_system)
  const running = run !== null && !bulk_coding_wiz_is_finished(bulk_coding_wiz_counts(run))

  function handle_coding_wiz() {
    if (start_bulk_coding_wiz_for_rows(rows, prefs, true)) {
      capture_event("coding_strip_coding_wiz_started", { rows: rows.length })
    }
  }

  return (
    <ol class={`mx-4 mb-3 grid shrink-0 grid-cols-1 gap-2 sm:grid-cols-3 ${className ?? ""}`}>
      <Step n={1} active={stage === 1} done={stage > 1} title="Auto code" detail="Your coding rules and reason-code mappings.">
        <Button variant={stage === 1 ? "default" : "outline"} size="sm" onClick={on_auto_code} disabled={codeable === 0}>
          <Wand2 className="h-4 w-4" />
          Preview auto code
        </Button>
      </Step>
      <Step
        n={2}
        active={stage === 2}
        done={stage > 2}
        title="CodingWiz on the rest"
        detail={rows.length === 0 ? "Nothing left to code." : `${rows.length} line${rows.length === 1 ? "" : "s"} still uncoded.`}
      >
        <Button
          variant={stage === 2 ? "default" : "outline"}
          size="sm"
          onClick={handle_coding_wiz}
          disabled={rows.length === 0 || running}
          title="Runs CodingWiz on every uncoded line with the team defaults (Settings › Accounting › CodingWiz defaults)"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {running ? "Running…" : `Run on ${rows.length} uncoded`}
        </Button>
      </Step>
      <Step
        n={3}
        active={stage === 3}
        done={false}
        title={review_step.title}
        detail={review_step.detail}
      />
    </ol>
  )
}

function Step({
  n,
  active,
  done,
  title,
  detail,
  children,
}: {
  n: number
  active: boolean
  done: boolean
  title: string
  detail: string
  children?: preact.ComponentChildren
}) {
  const ring = active ? "border-blue-300 bg-blue-50" : done ? "border-emerald-200 bg-emerald-50/50" : "border-gray-200 bg-white"
  const badge = active ? "bg-blue-600 text-white" : done ? "bg-emerald-600 text-white" : "bg-gray-200 text-gray-700"
  return (
    <li class={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${ring}`}>
      <div class="flex min-w-0 items-start gap-2">
        <span class={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${badge}`}>
          {done ? "✓" : n}
        </span>
        <div class="min-w-0">
          <div class="text-sm font-medium text-gray-900">{title}</div>
          <div class="truncate text-xs text-gray-600">{detail}</div>
        </div>
      </div>
      {children}
    </li>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  describe("review_step_copy", () => {
    it("points at Preview cash application when an accounting system is connected", () => {
      expect(review_step_copy(0, true)).toEqual({
        title: "Review and apply",
        detail: "Every line is coded. Press Preview cash application above.",
      })
      expect(review_step_copy(3, true).detail).toBe("Fix any line by hand, then Preview cash application.")
    })
    it("points at Export CSV and the manual applied mark when none is connected", () => {
      expect(review_step_copy(0, false)).toEqual({
        title: "Review and export",
        detail: "Every line is coded. Export CSV below, then Mark as applied.",
      })
      expect(review_step_copy(3, false).detail).toBe("Fix any line by hand, then Export CSV and Mark as applied.")
    })
  })
  describe("coding_strip_stage", () => {
    it("starts at auto code, moves to CodingWiz once tried, and to review when nothing is uncoded", () => {
      expect(coding_strip_stage(5, 5, false)).toBe(1)
      expect(coding_strip_stage(5, 2, true)).toBe(2)
      expect(coding_strip_stage(5, 0, false)).toBe(3)
      expect(coding_strip_stage(0, 0, false)).toBe(3)
    })
  })
  describe("uncoded_rows", () => {
    it("keeps uncoded deductions and discounted invoices, drops coded rows and plain invoices", () => {
      const rows = uncoded_rows([
        { id: "a", kind: "deduction", accounting_status: "uncoded", invoice_amount: -5, invoice_number: "A" },
        { id: "b", kind: "deduction", accounting_status: "coded", invoice_amount: -5, invoice_number: "B" },
        { id: "c", kind: "invoice", accounting_status: "uncoded", invoice_amount: 100, discount_amount: 2, invoice_number: "C" },
        { id: "d", kind: "invoice", accounting_status: "uncoded", invoice_amount: 100, discount_amount: 0, invoice_number: "D" },
      ] as unknown as TransactionJson[])
      expect(rows.map((r: BulkCodingRow) => r.id)).toEqual(["a", "c"])
    })
  })
}
